/**
 * The account button, which on a phone is also the overflow menu.
 *
 * Moving the navigation to a bottom bar created a trap: the bar holds five
 * tiles, so everything else became unreachable on a phone. A parent — whose
 * only destinations are Notifications and Paramètres — ended up with no
 * navigation at all, and even a director could not open Paramètres.
 *
 * So this menu lists every destination the bottom bar did NOT get to, and the
 * invariant is simple: on any screen size, every route the user may open is
 * reachable without typing a URL. On desktop the top bar already covers them,
 * so the menu holds only the account actions.
 *
 * A director's overflow is 21 entries, which on a 812px-tall phone ran off the
 * bottom and took "Se déconnecter" with it. The list is therefore bounded and
 * scrollable, with sign-out pinned OUTSIDE the scroll area — the one action
 * that must never be unreachable. Entries keep their desktop group headings,
 * because a flat list of 21 French labels is a wall, not a menu.
 */
import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import type { AppRoute } from "./routes";

/** Preserve the caller's order (already sorted by nav.order) within each group. */
function groupOf(routes: AppRoute[]): [string, AppRoute[]][] {
  const groups = new Map<string, AppRoute[]>();
  for (const route of routes) {
    const key = route.nav?.group ?? "";
    groups.set(key, [...(groups.get(key) ?? []), route]);
  }
  return [...groups.entries()];
}

export function AccountMenu({
  initials, overflow, onSignOut,
}: {
  initials: string;
  /** Routes with no bottom-bar tile — shown on phones only. */
  overflow: AppRoute[];
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Mon compte"
        className="grid h-8 w-8 place-items-center rounded-full bg-accent text-xs font-bold"
        onClick={() => setOpen((current) => !current)}
      >
        {initials || "?"}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-1 min-w-56 rounded-lg border border-border bg-surface py-1 shadow-lg"
        >
          {/* Phones only: on desktop these already live in the top bar, and
              repeating them here would be two routes to the same place. */}
          {overflow.length > 0 && (
            <div className="md:hidden">
              <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
                {groupOf(overflow).map(([group, routes]) => (
                  <div key={group}>
                    {group && (
                      <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-text-faint">
                        {group}
                      </div>
                    )}
                    {routes.map((route) => (
                      <NavLink
                        key={route.path}
                        to={route.path}
                        role="menuitem"
                        className={({ isActive }) =>
                          `block px-3 py-2 text-sm ${
                            isActive
                              ? "bg-surface-muted font-semibold text-text-strong"
                              : "text-text-secondary hover:bg-surface-muted"
                          }`
                        }
                        onClick={() => setOpen(false)}
                      >
                        {route.nav!.label}
                      </NavLink>
                    ))}
                  </div>
                ))}
              </div>
              <div className="my-1 border-t border-border" />
            </div>
          )}

          {/* Outside the scroll area on purpose. */}
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3 py-2 text-left text-sm text-danger hover:bg-surface-muted"
            onClick={() => { setOpen(false); onSignOut(); }}
          >
            Se déconnecter
          </button>
        </div>
      )}
    </div>
  );
}
